/**
 * One WebSocket to /ws for the whole tab. Components subscribe to mints (ref-counted) and listen to messages.
 *
 * Reconnects with exponential backoff and jitter (1 s doubling to 30 s) after any drop: a refused upgrade (8 per IP,
 * 5,000 overall; the browser only sees a close before `open`), a 1008 close for sending too fast, or a network drop.
 * The backoff resets only once a socket has stayed open for 30 s. After a reconnect it resubscribes and tells the
 * reconnect listeners, since the server does not replay what was missed.
 */
import { wsUrl } from './client';
import type { WsClientMessage, WsServerMessage } from './types';

export type LiveStatus = 'idle' | 'connecting' | 'open' | 'closed';
type Listener = (m: WsServerMessage) => void;

/** Delay before reconnect attempt `retry` (0-based): 1 s doubling to 30 s, with up to 50% random jitter removed. */
export function reconnectDelay(retry: number, random = Math.random): number {
  const ceiling = Math.min(30_000, 1000 * 2 ** retry);
  return Math.round(ceiling * (0.5 + random() / 2));
}

const STABLE_MS = 30_000;

class LiveClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<(s: LiveStatus) => void>();
  private reconnectListeners = new Set<() => void>();
  private mints = new Map<string, number>();
  private all = 0;
  private retry = 0;
  private everOpened = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  status: LiveStatus = 'idle';

  private setStatus(s: LiveStatus) {
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }

  start() {
    if (this.ws || this.timer || typeof WebSocket === 'undefined') return;
    this.setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.setStatus('open');
      this.stableTimer = setTimeout(() => {
        this.retry = 0;
        this.stableTimer = null;
      }, STABLE_MS);
      if (this.all > 0) this.send({ type: 'subscribe', topic: 'all' });
      if (this.mints.size) this.send({ type: 'subscribe', mints: [...this.mints.keys()] });
      if (this.everOpened) {
        for (const l of this.reconnectListeners) {
          try {
            l();
          } catch (e) {
            console.warn('live reconnect listener failed', e);
          }
        }
      }
      this.everOpened = true;
    };
    ws.onmessage = (ev) => {
      let m: WsServerMessage;
      try {
        m = JSON.parse(String(ev.data)) as WsServerMessage;
      } catch {
        return;
      }
      for (const l of this.listeners) {
        try {
          l(m);
        } catch (e) {
          console.warn('live listener failed', e);
        }
      }
    };
    ws.onclose = () => {
      this.ws = null;
      if (this.stableTimer) {
        clearTimeout(this.stableTimer);
        this.stableTimer = null;
      }
      this.setStatus('closed');
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        // closing anyway
      }
    };
  }

  private scheduleReconnect() {
    if (this.timer) return;
    const delay = reconnectDelay(this.retry++);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.start();
    }, delay);
  }

  private send(m: WsClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onStatus(l: (s: LiveStatus) => void): () => void {
    this.statusListeners.add(l);
    return () => this.statusListeners.delete(l);
  }

  /** Called after every reconnect (not the first connect): refetch what the socket may have missed. */
  onReconnect(l: () => void): () => void {
    this.reconnectListeners.add(l);
    return () => this.reconnectListeners.delete(l);
  }

  subscribeAll(): () => void {
    this.all++;
    if (this.all === 1) this.send({ type: 'subscribe', topic: 'all' });
    return () => {
      this.all = Math.max(0, this.all - 1);
      if (this.all === 0) this.send({ type: 'unsubscribe', topic: 'all' });
    };
  }

  subscribeMints(mints: string[]): () => void {
    const fresh: string[] = [];
    for (const m of mints) {
      const n = this.mints.get(m) ?? 0;
      this.mints.set(m, n + 1);
      if (n === 0) fresh.push(m);
    }
    if (fresh.length) this.send({ type: 'subscribe', mints: fresh });
    return () => {
      const gone: string[] = [];
      for (const m of mints) {
        const n = (this.mints.get(m) ?? 1) - 1;
        if (n <= 0) {
          this.mints.delete(m);
          gone.push(m);
        } else this.mints.set(m, n);
      }
      if (gone.length) this.send({ type: 'unsubscribe', mints: gone });
    };
  }
}

export const live = new LiveClient();
